import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building, ChevronRight, Plus } from 'lucide-react';
import { loadCustomerOptions, loadCustomers, loadOrganizationTypes, loadOrganizations } from './api';
import CustomerPage, { OrganizationForm } from './XomicsCustomer';
import { useLabSearch } from './LaboratoryParts';
import { Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './organizations.css';

const text = (key, values) => tx(`ui.org.${key}`, values);

// The customers. Choosing one turns the page into that customer (XomicsCustomer): its projects,
// invoices, accounts and details; back returns to the list as it was. Beside each row: its
// projects, those waiting for their samples, and invoices still to be paid. Adding a customer is
// a form over the page; who our invoices are issued by is under Finance (billing profiles).
export default function XomicsOrganizations() {
  const [params] = useSearchParams();
  const asked = params.get('q') || '';
  const { query, setQuery, search } = useLabSearch(asked);
  // ?customer=<id> (from Finance's receivables) opens that customer at once.
  const [active, setActive] = useState(asked ? 'all' : 'active'), [form, setForm] = useState(null);
  const [selected, setSelected] = useState(() => (Number(params.get('customer')) > 0 ? Number(params.get('customer')) : null));
  const load = useCallback(() => loadOrganizations({ query: search, active }), [search, active]);
  const resource = useLiveResource(['organizations'], load);
  const open = useLiveResource(['organizations'], loadCustomers);
  const businessOf = new Map((open.data || []).map(customer => [customer.id, customer]));
  const [types, setTypes] = useState([]);
  const [options, setOptions] = useState(null);
  useEffect(() => { loadOrganizationTypes().then(setTypes).catch(() => setTypes([])); }, []);
  useEffect(() => { loadCustomerOptions().then(setOptions).catch(() => setOptions({ workflows: [], currencies: [], staff: [] })); }, []);
  const data = resource.data;
  const refresh = () => { resource.refresh(); open.refresh(); };
  const forms = <>
    {form === 'customer' && <OrganizationForm organization={null} types={types} onCancel={() => setForm(null)}
      onSaved={saved => { setForm(null); refresh(); setSelected(saved.id); }} />}
  </>;
  if (selected !== null) {
    return <div className="xo-products-page xo-org-page">
      <CustomerPage id={selected} types={types} options={options} onBack={() => setSelected(null)} onChanged={refresh} />
      {forms}
    </div>;
  }
  return <div className="xo-products-page xo-org-page">
    <Head title={tx('ui.customers.title')} subtitle={tx('ui.customers.subtitle_projects')}>
      {data !== null && data.mayManage && <button type="button" className="xo-btn" onClick={() => setForm('customer')}><Plus aria-hidden="true" />{tx('ui.customers.add')}</button>}
    </Head>
    <div className="xo-product-navigation">
      <div className="xo-product-toolbar"><SearchField className="xo-product-search" value={query} onChange={setQuery} maxLength={100} placeholder={text('search')} /></div>
      <div className="xo-product-section-row"><WorkspaceTabs className="xo-product-sections" value={active} onChange={setActive} label={text('filter')}
        tabs={['active', 'inactive', 'all'].map(value => ({ value, label: text(`filter_${value}`) }))} /></div>
    </div>
    {resource.error ? <Failed message={resource.error} onRetry={resource.refresh} /> : data === null ? <Loading label={text('loading')} />
      : data.organizations.length === 0 ? <Empty title={text(search ? 'no_matches' : 'empty')} detail={text('empty_hint')} />
        : <ul className="xo-org-list">{data.organizations.map(organization => {
          const business = businessOf.get(organization.id);
          return <li key={organization.id}>
            <button type="button" onClick={() => setSelected(organization.id)} aria-label={`${organization.name} · ${organization.code}`}>
              <span className="xo-org-icon"><Building aria-hidden="true" /></span>
              <span className="xo-org-identity"><strong>{organization.name}</strong><code>{organization.code}</code></span>
              <span className="xo-org-type">{organization.type.label} · {organization.countryCode}</span>
              <span className="xo-org-contact">{organization.contact.name}<small>{organization.contact.email}</small></span>
              <span className="xo-org-count">{tx(business?.submissions === 1 ? 'ui.customers.projects_count_one' : 'ui.customers.projects_count', { count: business ? business.submissions : 0 })}
                {business && (business.waitingForSamples > 0 || business.invoicesDue > 0) && <span className="xo-org-flags">
                  {business.waitingForSamples > 0 && <Pill tone="info">{tx('ui.customers.waiting_count', { count: business.waitingForSamples })}</Pill>}
                  {business.invoicesDue > 0 && <Pill tone="warn">{tx('ui.customers.unpaid_count', { count: business.invoicesDue })}</Pill>}
                </span>}</span>
              <Pill tone={organization.isActive ? 'ok' : 'flat'}>{text(organization.isActive ? 'is_active' : 'is_inactive')}</Pill>
              <ChevronRight aria-hidden="true" />
            </button>
          </li>;
        })}</ul>}
    {forms}
  </div>;
}
